import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Debugging
import debug from 'debug';

// Types
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { resolveFreshRegisterDate } from '../../../../../utils/freshRegisterDate';
import { AuthContextType } from '../../../../../hooks/useAuth';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';

// Selector
import { selectActiveOperatorsAndVehicles } from '../../../../../gql/selectors/actions';

// Static data
import { hoursType } from '../../../../../data/orders';
import { GET_EXTERNAL_COMPANIES } from '../../../../../gql/queries/actions/externalCompanies';

// GQL
import { client } from '../../../../../gql/client';
import { GET_CONCESSIONS } from '../../../../../gql/queries/orders/orders';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACTION_CREATION_SCOPE,
  ACTION_MEDIA_SCOPE,
  readCreationDraftId,
} from '../../../../../utils/draftScopes';
import { foreignCreationParentRedirect } from '../creationDraft';

// Configure debug namespaces
const logLoader = debug('rois:operators:newaction:loader');
const logData = debug('rois:operators:newaction:data');
const logExternal = debug('rois:operators:newaction:external');
const logSession = debug('rois:operators:newaction:session');
const logError = debug('rois:operators:newaction:error');
const logDebug = debug('rois:action:newaction:debug');

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      logLoader('👨‍💼 Cargando operadores internos y empresas externas');

      const internalOperators =
        (await selectActiveOperatorsAndVehicles(true)) ?? { vehicles: [], operators: [], teams: [] };

      logData(
        `📊 Operadores internos cargados: ${Array.isArray(internalOperators)
          ? internalOperators.length
          : 'objeto estructurado'
        }`
      );

      const { readExternalCompanies } = client.readQuery({
        query: GET_EXTERNAL_COMPANIES,
      });

      const sortedExternalCompanies = [...readExternalCompanies].sort((a, b) =>
        a.companyName.localeCompare(b.companyName)
      );

      logExternal(
        `📊 Empresas externas cargadas: ${sortedExternalCompanies.length}`
      );

      return {
        internalOperators,
        externalCompanies: sortedExternalCompanies,
        hoursTypeOptions: hoursType,
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    logError('❌ Error cargando operadores y vehículos:', err);
    return true;
  }
};

export const OperatorsAndVehiclesLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params, request }) => {
  // Un borrador de OTRO padre no se abre bajo esta URL: atrás, una URL
  // compartida o el «Editar» de un resumen viejo enseñaban — y guardaban — el
  // borrador de la OT Y bajo la de la X (auditoría de creación 2.3).
  const foreignParent = foreignCreationParentRedirect({ params, request });
  if (foreignParent) return foreignParent;
  // Las copias de las pestañas, la lista de fotos y el croquis son del
  // borrador en curso (`draftId`): si eran de otro, fuera, antes de que nadie
  // las lea. Síncrono y antes de cualquier `await`: el loader de `details` y el
  // de la pestaña corren en paralelo (spec 2026-11-02 wizard-draft-ownership).
  const draftId = readCreationDraftId();
  claimDraftScope(ACTION_CREATION_SCOPE, draftId);
  claimDraftScope(ACTION_MEDIA_SCOPE, draftId);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Activamos logs en desarrollo
  if (import.meta.env.DEV) {
    debug.enable('rois:operators:newaction:*,rois:action:newaction:*,rois:action:operators-table:*');
    logLoader('🚀 Debug activado para OperatorsAndVehiclesLoader (NewAction)');
  }

  logLoader('🔄 Iniciando OperatorsAndVehiclesLoader para Nueva Actuación');
  try {

  // Comprobamos si hay datos en sessionStorage para las pestañas individuales
  const tabInternoData = sessionStorage.getItem(
    `${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_interno`
  );

  const tabExternalsData = sessionStorage.getItem(
    `${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_externals`
  );

  // Comprobamos datos del formulario completo
  const stringifyForm = sessionStorage.getItem(
    formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM
  );

  if (tabInternoData) {
    logSession('💾 Datos específicos de pestaña Interno encontrados');
  }

  if (tabExternalsData) {
    logSession('💾 Datos específicos de pestaña Externos encontrados');
  }

  if (stringifyForm) {
    logSession('💾 Datos completos del formulario encontrados');
  }

  const {
    data: { concessions },
  } = await client.query({
    query: GET_CONCESSIONS,
  });

  logData(`📊 Concesiones cargadas: ${concessions.length}`);

  const loaderToResolve = await loadFormOptions(userData);
  const breadCrumbsDataStringify = sessionStorage.getItem(
    formActionNaming.NEW_ACTION_DATA
  );

  let sessionStorageData;
  let breadCrumbsData;

  if (stringifyForm) {
    sessionStorageData = JSON.parse(decompressSessionStorage(stringifyForm));
    logSession('📥 Datos cargados de sessionStorage');
  }

  if (breadCrumbsDataStringify) {
    breadCrumbsData = JSON.parse(
      decompressSessionStorage(breadCrumbsDataStringify)
    );
    logSession('📥 Datos de breadCrumbs cargados');
  }

  // Aseguramos de que no hay datos duplicados en externals
  if (
    sessionStorageData &&
    sessionStorageData.externals &&
    sessionStorageData.externals.length > 0
  ) {
    logExternal(
      `🔍 Verificando duplicados en empresas externas (${sessionStorageData.externals.length} elementos)`
    );

    // Utilizamos un Map para la deduplicación con claves compuestas
    const uniqueExternalsMap = new Map();

    sessionStorageData.externals.forEach(
      (company: {
        externalCompanyIDs?: string[];
        id: string;
        company?: string;
      }) => {
        // Usamos la combinación de ID de compañía + ID único como clave de deduplicación
        const companyId =
          company.externalCompanyIDs && company.externalCompanyIDs[0];
        const uniqueKey = `${companyId}_${company.id}`;

        if (!uniqueExternalsMap.has(uniqueKey)) {
          uniqueExternalsMap.set(uniqueKey, company);
        } else {
          logExternal(
            `⚠️ Encontrado duplicado para empresa: ${company.company || 'Sin nombre'
            }`
          );
        }
      }
    );

    // Convertimos el Map de vuelta a array
    const deduplicatedExternals = Array.from(uniqueExternalsMap.values());

    if (deduplicatedExternals.length !== sessionStorageData.externals.length) {
      logExternal(
        `🧹 Eliminados ${sessionStorageData.externals.length - deduplicatedExternals.length
        } duplicados`
      );
      // Actualizamos los datos
      sessionStorageData.externals = deduplicatedExternals;
    } else {
      logExternal('✅ No se encontraron duplicados');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔬 DEBUG - DIAGNÓSTICO ROL TABLET: OPERARIOS Y VEHÍCULOS NO CARGAN
  // ═══════════════════════════════════════════════════════════════════════════
  // Verificar estructura de loaderToResolve
  const isLoaderObject = typeof loaderToResolve === 'object' && loaderToResolve !== null;
  const internalOps = isLoaderObject ? loaderToResolve.internalOperators : null;
  const isInternalOpsArray = Array.isArray(internalOps);

  logDebug('═══ ROIS DEBUG: NewAction OperatorsAndVehicles Loader ═══');
  logDebug('LOADER_TO_RESOLVE: %O', {
    tipo: typeof loaderToResolve,
    esObjeto: isLoaderObject,
    internalOperators_tipo: internalOps ? (isInternalOpsArray ? 'ARRAY' : 'OBJETO') : 'null',
    internalOperators_keys: internalOps ? Object.keys(internalOps) : 'N/A',
  });

  // Si es objeto, mostrar contenido de operators, vehicles, teams
  if (isLoaderObject && internalOps && !isInternalOpsArray) {
    logDebug('INTERNAL_OPERATORS (objeto): %O', {
      operators_count: internalOps.operators?.length || 0,
      vehicles_count: internalOps.vehicles?.length || 0,
      teams_count: internalOps.teams?.length || 0,
      teams: internalOps.teams?.map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })) || [],
    });
  }

  // Datos de sessionStorage
  logDebug('SESSION_STORAGE: %O', {
    tiene_datos: !!sessionStorageData,
    keys: sessionStorageData ? Object.keys(sessionStorageData) : [],
    operatorsTable_count: sessionStorageData?.operatorsTable?.filter((x: unknown) => x)?.length || 0,
    vehicleTable_keys: Object.keys(sessionStorageData?.vehicleTable || {}).length,
  });

  // Valor final de teams que se extraerá
  const teamsValue = (isLoaderObject && internalOps && !isInternalOpsArray)
    ? internalOps.teams || []
    : [];

  logDebug('TEAMS FINAL: %O', {
    teams_count: teamsValue.length,
    teams: teamsValue.map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })),
    PROBLEMA_DETECTADO: teamsValue.length === 0 ? '⚠️ TEAMS VACÍO - Auto-filtro Tablet NO funcionará!' : 'OK',
  });
  logDebug('═══════════════════════════════════════════════════════════════');
  // ═══════════════════════════════════════════════════════════════════════════

  // Construye datos combinados
  const combinedData = {
    ...sessionStorageData,
    ...(typeof loaderToResolve === 'object' ? loaderToResolve : {}),
    // La fecha de la actuación vive en ESTE paso (el primero la oculta con
    // `!isAction`). Va después del borrador para ganarle: su fecha pudo quedar
    // congelada en una visita anterior. Sin borrador, `sessionStorageData` es
    // `undefined` y el helper da la de ahora — una llamada cubre las dos ramas.
    ...resolveFreshRegisterDate(sessionStorageData),
    breadCrumbsData: breadCrumbsData
      ? {
        orderParentId: breadCrumbsData.orderParentId,
        orderParentRoid: breadCrumbsData.orderParentRoid,
        concessions: breadCrumbsData.concessions,
        concession: breadCrumbsData.concession,
      }
      : {},
    concessions,
    // FIX: Extract teams from internalOperators to top level (component expects initialData.teams)
    // Note: internalOperators can be either an array [] or object { vehicles, operators, teams }
    teams:
      typeof loaderToResolve === 'object' &&
        loaderToResolve?.internalOperators &&
        !Array.isArray(loaderToResolve.internalOperators)
        ? loaderToResolve.internalOperators.teams || []
        : [],
  };

  logLoader('✅ Loader de OperatorsAndVehicles (NewAction) completado');

  return combinedData;
  } catch (err) {
    console.log('Loader offline fallback:', err);
    const breadCrumbsDataStringify = sessionStorage.getItem(formActionNaming.NEW_ACTION_DATA);
    const breadCrumbsData = breadCrumbsDataStringify ? JSON.parse(decompressSessionStorage(breadCrumbsDataStringify)) : {};
    return { internalOperators: { operators: [], vehicles: [], teams: [] }, externalCompanies: [], hoursTypeOptions: hoursType, concessions: [], teams: [], breadCrumbsData: { orderParentId: breadCrumbsData?.orderParentId, orderParentRoid: breadCrumbsData?.orderParentRoid } };
  }
};

  return { data: load() };
};
